import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '../../services/analytics.service';
import { EventsService } from '../../services/events.service';
import { CityEventsService } from '../../services/city-events.service';
import { EventCalendarService } from '../../services/event-calendar.service';
import { EVENTS_PAGE_SIZE, EventDoc } from '../../models/event.model';
import {
  CityEventsSection,
  appendCityEventsPage,
  cityEventsAllowed,
  cityEventsSegmentVisible,
  emptyCityEventsSection,
} from '../../models/city-event.model';

/** Which programme the screen is showing: the venue's own, or the city's. */
export type EventsSegment = 'venue' | 'city';

/**
 * The events screen — the web port of the iOS Events & Deals modal (`EventsContentView`).
 *
 * Two sections: a horizontal strip of featured events over a paginated list of the rest. Replaces
 * the "download the app" page that used to own `/events` — that page and its Branch install link
 * are gone entirely; this screen shows events and nothing else.
 *
 * DEALS ARE NOT PORTED. The iOS modal nominally has an Events/Deals tab bar, but the bar is
 * `.hidden()` and nothing else mutates `selectedTab`, so `DealsContentView` is unreachable in the
 * shipping build. There is no live UX to match and no voucher lifecycle to mirror.
 *
 * Scoping mirrors iOS's non-tab branch, which is the only one the web has an analogue for — there
 * is no Events tab and no App Clip here. See `loadInitial`.
 */
@Component({
    selector: 'app-events-list',
    templateUrl: './events-list.component.html',
    styleUrls: ['./events-list.component.scss'],
    host: { class: 'events-scope' },
    standalone: false
})
export class EventsListComponent implements OnInit, OnDestroy {
  featured: EventDoc[] = [];
  all: EventDoc[] = [];

  loading = true;
  loadingMore = false;

  /** Non-null only when the initial load itself failed, as distinct from a venue with no events. */
  error: string | null = null;

  /** Transient confirmation after an .ics download, mirroring iOS's "Event added" alert. */
  toast: string | null = null;

  readonly skeletonCards = [1, 2, 3];

  // -------------------------------------------------------------------------
  // City segment
  //
  // A SECOND SOURCE on the same screen: the venue sections above come from Firestore, these two
  // come from Feather's own API. Kept in separate fields with separate paging on purpose — a city
  // failure can neither blank the venue lists nor stop them paging, and the venue path is not
  // touched at all. A venue with no city renders exactly today's screen.
  // -------------------------------------------------------------------------

  segment: EventsSegment = 'venue';

  cityFeatured: CityEventsSection = emptyCityEventsSection();
  cityAll: CityEventsSection = emptyCityEventsSection();

  private shopId: string | null = null;
  private allEventsEnabled = false;
  private allCursor: unknown | null = null;
  private toastTimer: any = null;

  /** Half one of the rule: the payload says this venue has a city. Decided before any request. */
  private cityAllowed = false;
  /** Half two: set once the initial city load has COMPLETED — success or failure, but finished. */
  private cityLoadFinished = false;

  /** Fire load-more this many pixels before the true bottom, matching the attractions list. */
  private static readonly LOAD_MORE_THRESHOLD_PX = 600;

  constructor(
    private eventsService: EventsService,
    private cityEventsService: CityEventsService,
    private calendar: EventCalendarService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private translate: TranslateService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    const raw = sessionStorage.getItem('clientData');
    let clientData: any = null;
    if (raw) {
      try {
        clientData = JSON.parse(raw);
      } catch {
        clientData = null;
      }
    }
    if (!clientData) {
      // No venue resolved: the splash owns that recovery, exactly as the attractions list does.
      this.router.navigate(['']);
      return;
    }

    // Logged only once the screen is actually going to render. The page this replaced fired it
    // unconditionally, so a session that bounced straight back to the splash still counted as an
    // events view — inflating the metric with visits nobody ever saw.
    this.analytics.log('events_screen_opened');

    // Firestore stores `businessId` as a string while the API sends a number — coerce once, here.
    this.shopId = clientData.id != null ? String(clientData.id) : null;
    this.allEventsEnabled = clientData.are_all_events_enabled === true;

    // Whether the City segment can exist at all is read off the payload, BEFORE any request — an
    // empty list cannot tell "this venue has no city" from "the city has nothing on" from a failure.
    this.cityAllowed = cityEventsAllowed(clientData);

    // Which segment to open on lives in the URL (`/events?segment=city`), so coming back from an
    // event's details — by the button or by browser Back — returns to the segment the diner left,
    // and a shared link opens where the sender was. A venue with no city ignores it.
    if (this.route.snapshot.queryParamMap.get('segment') === 'city' && this.cityAllowed) {
      this.segment = 'city';
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.loadInitial();
    this.loadCityInitial();
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onScroll);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  // -------------------------------------------------------------------------
  // loading
  // -------------------------------------------------------------------------

  /**
   * Both sections in parallel, as the iOS view model's task group does.
   *
   * The split depends on the venue's `are_all_events_enabled` flag:
   *   false (the common case) — this venue's featured events lead, its non-featured fill the list.
   *   true                    — this venue's events lead, and EVERY venue's events fill the list.
   *
   * Note the second case is the one iOS intends but never reaches: its `asyncFetchAllEvents` passes
   * `shopId: ""`, which is non-nil and so adds a `businessId == ""` filter that matches nothing. The
   * web passes null and gets the city-wide list the flag was designed to produce.
   */
  private async loadInitial(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.allCursor = null;

    const [featuredPage, allPage] = await Promise.all([
      this.eventsService.getEvents({
        shopId: this.shopId,
        isFeatured: this.allEventsEnabled ? null : true
      }),
      this.eventsService.getEvents({
        shopId: this.allEventsEnabled ? null : this.shopId,
        isFeatured: this.allEventsEnabled ? null : false
      })
    ]);

    // Only a total failure is the screen's error. One section failing while the other returns is a
    // partial result, and showing the half that worked beats blanking the page.
    if (!featuredPage && !allPage) {
      this.error = this.translate.instant('events.error');
    }

    this.featured = featuredPage?.events ?? [];
    this.all = allPage?.events ?? [];
    this.allCursor = allPage?.cursor ?? null;

    // With the city-wide flag on, the same event can lead the strip AND appear in the list below.
    // Drop the duplicates from the list; the strip is the more prominent placement.
    if (this.allEventsEnabled && this.featured.length) {
      const featuredIds = new Set(this.featured.map(e => e.id).filter(Boolean));
      this.all = this.all.filter(e => !featuredIds.has(e.id));
    }

    this.loading = false;
  }

  /**
   * Next page of the main list only. The featured strip is deliberately a single page: it is a
   * carousel of a handful of curated events, and paging it horizontally buys nothing.
   */
  private async loadNextPage(): Promise<void> {
    if (this.loading || this.loadingMore || !this.allCursor) return;

    this.loadingMore = true;
    const page = Math.floor(this.all.length / EVENTS_PAGE_SIZE) + 1;

    const result = await this.eventsService.getEvents({
      shopId: this.allEventsEnabled ? null : this.shopId,
      isFeatured: this.allEventsEnabled ? null : false,
      cursor: this.allCursor
    });

    if (result) {
      // Guard against a repeated cursor page: dedupe on id before appending.
      const seen = new Set(this.all.map(e => e.id).filter(Boolean));
      const featuredIds = new Set(this.featured.map(e => e.id).filter(Boolean));
      this.all = this.all.concat(
        result.events.filter(e => !seen.has(e.id) && !(this.allEventsEnabled && featuredIds.has(e.id)))
      );
      this.allCursor = result.cursor;

      // Success only: a page that failed to load was not browsed.
      this.analytics.log('events_list_paged', { list_page: page });
    } else {
      // Stop paging on failure rather than retrying the same cursor on every scroll tick.
      this.allCursor = null;
    }

    this.loadingMore = false;
  }

  // -------------------------------------------------------------------------
  // loading — City
  // -------------------------------------------------------------------------

  /**
   * Both city sections in parallel, alongside the venue load rather than on the first tap.
   *
   * ⚠️ **Eager on purpose, and this is the one place Track M diverges from both mobile apps** (they
   * load lazily on the first segment switch). The rule is "hide the segment when the city has no
   * events", and a lazy load can only discover that *after* the diner has tapped City — hiding the
   * control they just pressed is worse than either alternative. The cost is two requests on every
   * touristic venue's events screen, which the endpoint caches for ~5 minutes per (shop, segment,
   * featured, page).
   *
   * `segment: 'upcoming'` only. `past` exists on the API and no client renders it in v1.
   */
  private async loadCityInitial(): Promise<void> {
    if (!this.cityAllowed || !this.shopId) return;

    this.cityLoadFinished = false;
    this.cityFeatured = { ...emptyCityEventsSection(), loading: true };
    this.cityAll = { ...emptyCityEventsSection(), loading: true };

    const [featuredPage, allPage] = await Promise.all([
      this.cityEventsService.getCityEvents(this.shopId, { segment: 'upcoming', featured: true }),
      this.cityEventsService.getCityEvents(this.shopId, { segment: 'upcoming', featured: false })
    ]);

    this.cityFeatured = appendCityEventsPage(this.cityFeatured, featuredPage);
    this.cityAll = appendCityEventsPage(this.cityAll, allPage);

    // Set LAST, and only here: it is what unlocks the "hide when empty" half of the rule, so it must
    // never be true while a request is still in flight.
    this.cityLoadFinished = true;

    // If that load took the segment away (a retry that came back empty) the diner cannot be left
    // looking at a segment with no control to leave it by.
    if (this.segment === 'city' && !this.showsCitySegment) {
      this.segment = 'venue';
      // Drop the param too, or Back from an event would return to a segment that no longer exists.
      this.syncSegmentToUrl();
    }
  }

  /**
   * Next page of a city section. `offset` comes off the section (server rows handed out so far), and
   * the stop condition is `hasMore` and nothing else (**I-11**).
   */
  private async loadNextCityPage(featured: boolean): Promise<void> {
    const section = featured ? this.cityFeatured : this.cityAll;
    if (!this.shopId || section.loading || section.loadingMore || !section.hasMore) return;

    if (featured) {
      this.cityFeatured = { ...section, loadingMore: true };
    } else {
      this.cityAll = { ...section, loadingMore: true };
    }

    const page = await this.cityEventsService.getCityEvents(this.shopId, {
      segment: 'upcoming',
      featured,
      offset: section.offset
    });

    if (featured) {
      this.cityFeatured = appendCityEventsPage(this.cityFeatured, page);
    } else {
      this.cityAll = appendCityEventsPage(this.cityAll, page);
    }
  }

  /**
   * The featured strip's own next page, fired as the carousel nears its right-hand end.
   *
   * The venue strip is deliberately a single page (a handful of curated events, and paging it
   * horizontally buys nothing), but a city's featured section carries a real `hasMore` — a festival
   * programme can have more featured events than one page holds.
   */
  onCityCarouselScroll(event: Event): void {
    const rail = event.target as HTMLElement;
    const remaining = rail.scrollWidth - (rail.scrollLeft + rail.clientWidth);
    if (remaining < EventsListComponent.LOAD_MORE_THRESHOLD_PX) {
      this.loadNextCityPage(true);
    }
  }

  private onScroll = (): void => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const clientHeight = document.documentElement.clientHeight;
    const scrollHeight = document.documentElement.scrollHeight;

    if (scrollHeight - (scrollTop + clientHeight) < EventsListComponent.LOAD_MORE_THRESHOLD_PX) {
      // Only the visible segment pages: the other one's list is not on screen, so scrolling says
      // nothing about wanting more of it.
      if (this.segment === 'city') {
        this.loadNextCityPage(false);
      } else {
        this.loadNextPage();
      }
    }
  };

  // -------------------------------------------------------------------------
  // state
  // -------------------------------------------------------------------------

  get isEmpty(): boolean {
    return !this.loading && !this.featured.length && !this.all.length;
  }

  get hasFeatured(): boolean {
    return this.loading || this.featured.length > 0;
  }

  get hasAll(): boolean {
    return this.loading || this.all.length > 0;
  }

  // -------------------------------------------------------------------------
  // state — City
  // -------------------------------------------------------------------------

  /**
   * Whether the segment control is on screen at all.
   *
   * Both halves of GZ's rule, in one tested function: the payload says there is a city, AND a
   * FINISHED load found something. A venue with no city never reaches this — no control, and today's
   * screen renders unchanged.
   */
  get showsCitySegment(): boolean {
    return cityEventsSegmentVisible(
      this.cityAllowed,
      this.cityLoadFinished,
      this.cityFeatured,
      this.cityAll
    );
  }

  get cityLoading(): boolean {
    return this.cityFeatured.loading || this.cityAll.loading;
  }

  /** Nothing in either section after a finished load. Only ever shown when nothing failed. */
  get isCityEmpty(): boolean {
    return (
      this.cityLoadFinished &&
      !this.showsCityError &&
      !this.cityFeatured.events.length &&
      !this.cityAll.events.length
    );
  }

  /**
   * The segment's error state — a failure with nothing to show for it. One section failing while the
   * other returns is a partial result, and showing the half that worked beats blanking the segment,
   * exactly as the venue path treats its two Firestore queries.
   */
  get showsCityError(): boolean {
    return (
      (this.cityFeatured.failed || this.cityAll.failed) &&
      !this.cityFeatured.events.length &&
      !this.cityAll.events.length
    );
  }

  get hasCityFeatured(): boolean {
    return this.cityLoading || this.cityFeatured.events.length > 0;
  }

  get hasCityAll(): boolean {
    return this.cityLoading || this.cityAll.events.length > 0;
  }

  // -------------------------------------------------------------------------
  // actions
  // -------------------------------------------------------------------------

  /**
   * `entryPoint` separates the curated strip from the plain list — whether featuring an event
   * actually earns its placement is the question the two sections exist to answer, so they must not
   * share one anonymous handler.
   */
  onOpen(eventId: string, entryPoint: 'featured' | 'all' | 'city_featured' | 'city_all'): void {
    this.analytics.log('event_open', { content_id: eventId, entry_point: entryPoint });

    // The details screen is reached with an id alone and has to fetch — and a city event is not in
    // Firestore. Telling it which source to try first turns the common path back into one request;
    // a shared link or a push carries no state and the screen tries both.
    const fromCity = entryPoint.startsWith('city_');
    this.router.navigate(['/event', eventId], fromCity ? { state: { source: 'city' } } : {});
  }

  onAddToCalendar(event: EventDoc): void {
    const saved = this.calendar.save(event);
    if (saved) {
      this.analytics.log('event_calendar_saved', { content_id: event.id ?? '' });
    }
    this.showToast(
      this.translate.instant(saved ? 'events.calendarSaved' : 'events.calendarUnavailable')
    );
  }

  private showToast(message: string): void {
    this.toast = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toast = null), 3000);
  }

  /**
   * Switch segments. A no-op on the segment already showing, so M4's analytics event counts real
   * changes rather than every render of the control.
   *
   * Nothing is fetched here: both programmes are already loaded (see `loadCityInitial`). Switching
   * is pure display, which is also what keeps a slow city response from blocking a tap.
   */
  selectSegment(segment: EventsSegment): void {
    if (segment === this.segment) return;
    this.segment = segment;
    this.syncSegmentToUrl();
    window.scrollTo(0, 0);

    // On a REAL change only — the screen always opens on Venue, so `events_screen_opened` cannot
    // answer whether anyone reads the city programme, and an event that also fired on re-selecting
    // the current segment would answer it wrongly. Same name and same lowercase `venue` / `city`
    // values iOS and Android send; the venue itself rides in the base bundle as `venue_id`.
    this.analytics.log('city_events_segment_switch', { segment });
  }

  /**
   * Write the current segment into the query string, REPLACING the history entry rather than adding
   * one: switching segments is not a navigation the diner should have to press Back through, and the
   * close button's `history.length > 1` test would start seeing entries this screen made itself.
   * Replacing still leaves the segment on the URL that Back returns to from the details screen,
   * which is the whole point.
   */
  private syncSegmentToUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { segment: this.segment === 'city' ? 'city' : null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  retry(): void {
    this.loadInitial();
  }

  /** The City segment's own retry — the venue lists are not reloaded and are not affected. */
  retryCity(): void {
    this.loadCityInitial();
  }

  /**
   * Closes the screen. iOS dismisses a modal here; the web equivalent is going back, except on a
   * deep link (push, shared URL) where there is nothing behind this page and `back()` would leave
   * the site entirely — so fall through to the menu.
   */
  back(): void {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/menu']);
    }
  }

  trackById(index: number, event: EventDoc): string {
    return event.id ?? String(index);
  }
}
